import React, { useState } from 'react';
import { colors } from '../utils/theme';
import { Lock, User, Eye, EyeOff, AlertCircle, ArrowLeft, Mail, Key, CheckCircle } from 'lucide-react';

export default function LoginPage({ onLogin }) {
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  // Esqueceu senha states
  const [modo, setModo] = useState('login'); // login, esqueci, resetar
  const [resetLogin, setResetLogin] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [resetMsg, setResetMsg] = useState(null);
  const [tokenDireto, setTokenDireto] = useState('');

  const API = window.location.origin + '/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!login || !senha) { setErro('Preencha login e senha'); return; }
    setLoading(true); setErro('');
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, senha }),
      });
      const data = await res.json();
      if (!res.ok) { setErro(data.error || 'Erro ao fazer login'); setLoading(false); return; }
      localStorage.setItem('xm_token', data.token);
      localStorage.setItem('xm_usuario', JSON.stringify(data.usuario));
      onLogin(data.usuario, data.token);
    } catch (err) { setErro('Erro de conexao com o servidor'); }
    setLoading(false);
  };

  const handleEsqueciSenha = async (e) => {
    e.preventDefault();
    if (!resetLogin && !resetEmail) { setErro('Informe o login ou o e-mail'); return; }
    setLoading(true); setErro(''); setResetMsg(null); setTokenDireto('');
    try {
      const res = await fetch(`${API}/auth/esqueci-senha`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: resetLogin, email: resetEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setErro(data.error || 'Erro'); setLoading(false); return; }

      setResetMsg(data);
      // Modo dev (sem SMTP): o backend devolve o link direto pra teste local.
      if (data.link) setTokenDireto(data.link);
    } catch (err) { setErro('Erro de conexao'); }
    setLoading(false);
  };

  const handleResetarSenha = async (e) => {
    e.preventDefault();
    if (!resetToken || !novaSenha) { setErro('Preencha o codigo e a nova senha'); return; }
    if (novaSenha.length < 4) { setErro('Senha deve ter pelo menos 4 caracteres'); return; }
    setLoading(true); setErro(''); setResetMsg(null);
    try {
      const res = await fetch(`${API}/auth/resetar-senha`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: resetLogin, token: resetToken, nova_senha: novaSenha }),
      });
      const data = await res.json();
      if (!res.ok) { setErro(data.error || 'Erro'); setLoading(false); return; }
      setResetMsg({ message: data.message, sucesso: true });
      setTimeout(() => { setModo('login'); setResetMsg(null); setLogin(resetLogin); }, 2000);
    } catch (err) { setErro('Erro de conexao'); }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px 12px 12px 38px', borderRadius: 8,
    border: `1px solid ${colors.border}`, background: colors.bg,
    color: colors.text, fontSize: 14, outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };

  const voltarLogin = () => { setModo('login'); setErro(''); setResetMsg(null); setTokenDireto(''); };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(135deg, ${colors.bg} 0%, #1a1a2e 50%, ${colors.bgLight} 100%)`,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{
        width: 400, padding: 40, borderRadius: 16,
        background: colors.card, border: `1px solid ${colors.border}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, margin: '0 auto 16px',
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 22, color: '#fff',
          }}>XM</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text }}>XM Controle</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: colors.textDim }}>
            {modo === 'login' ? 'Gestao Financeira de Cartoes' : modo === 'esqueci' ? 'Recuperar Senha' : 'Nova Senha'}
          </p>
        </div>

        {/* ======= TELA LOGIN ======= */}
        {modo === 'login' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Login</label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type="text" value={login} onChange={e => setLogin(e.target.value)} placeholder="Digite seu login" autoFocus
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = colors.primary}
                  onBlur={e => e.target.style.borderColor = colors.border} />
              </div>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Senha</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type={showSenha ? 'text' : 'password'} value={senha} onChange={e => setSenha(e.target.value)} placeholder="Digite sua senha"
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onFocus={e => e.target.style.borderColor = colors.primary}
                  onBlur={e => e.target.style.borderColor = colors.border} />
                <button type="button" onClick={() => setShowSenha(!showSenha)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: colors.textDim, padding: 4 }}>
                  {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {erro && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, color: colors.danger, fontSize: 13 }}>
                <AlertCircle size={16} /> {erro}
              </div>
            )}
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
              background: loading ? colors.textDim : `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
            }}>{loading ? 'Entrando...' : 'Entrar'}</button>

            <button type="button" onClick={() => { setModo('esqueci'); setErro(''); setResetLogin(login); }}
              style={{ width: '100%', marginTop: 12, padding: '10px', background: 'none', border: 'none', color: colors.primary, fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>
              Esqueceu a senha?
            </button>
          </form>
        )}

        {/* ======= TELA ESQUECI SENHA ======= */}
        {modo === 'esqueci' && (
          <form onSubmit={handleEsqueciSenha}>
            <button type="button" onClick={voltarLogin}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
              <ArrowLeft size={14} /> Voltar ao login
            </button>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Login</label>
              <div style={{ position: 'relative' }}>
                <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type="text" value={resetLogin} onChange={e => setResetLogin(e.target.value)} placeholder="Seu login (ou use o e-mail abaixo)" autoFocus
                  style={inputStyle} onFocus={e => e.target.style.borderColor = colors.primary} onBlur={e => e.target.style.borderColor = colors.border} />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>E-mail</label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="Ou seu e-mail cadastrado"
                  style={inputStyle} onFocus={e => e.target.style.borderColor = colors.primary} onBlur={e => e.target.style.borderColor = colors.border} />
              </div>
            </div>

            {erro && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, color: colors.danger, fontSize: 13 }}>
                <AlertCircle size={16} /> {erro}
              </div>
            )}

            {resetMsg && (
              <div style={{
                marginBottom: 16, padding: '12px 14px', borderRadius: 8, fontSize: 13,
                background: `${colors.info}15`, border: `1px solid ${colors.info}30`, color: colors.info,
              }}>
                <div>{resetMsg.message}</div>
                {tokenDireto && (
                  <div style={{ marginTop: 8, padding: '10px', background: colors.bg, borderRadius: 6 }}>
                    <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 6 }}>Modo dev (sem SMTP) — abra o link:</div>
                    <a href={tokenDireto} style={{ fontSize: 12, color: colors.primary, wordBreak: 'break-all' }}>{tokenDireto}</a>
                  </div>
                )}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
              background: loading ? colors.textDim : `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
            }}>{loading ? 'Solicitando...' : 'Enviar link de redefinição'}</button>
          </form>
        )}

        {/* ======= TELA RESETAR SENHA ======= */}
        {modo === 'resetar' && (
          <form onSubmit={handleResetarSenha}>
            <button type="button" onClick={() => { setModo('esqueci'); setErro(''); setResetMsg(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}>
              <ArrowLeft size={14} /> Voltar
            </button>

            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: `${colors.primary}10`, fontSize: 12, color: colors.textMuted }}>
              Resetando senha para: <b style={{ color: colors.text }}>{resetLogin}</b>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Codigo de Reset</label>
              <div style={{ position: 'relative' }}>
                <Key size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type="text" value={resetToken} onChange={e => setResetToken(e.target.value)} placeholder="Codigo de 6 digitos" autoFocus
                  style={{ ...inputStyle, fontSize: 18, letterSpacing: 4, textAlign: 'center', paddingLeft: 12 }} maxLength={6}
                  onFocus={e => e.target.style.borderColor = colors.primary} onBlur={e => e.target.style.borderColor = colors.border} />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.textMuted, marginBottom: 6 }}>Nova Senha</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
                <input type={showSenha ? 'text' : 'password'} value={novaSenha} onChange={e => setNovaSenha(e.target.value)} placeholder="Nova senha (min. 4 caracteres)"
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onFocus={e => e.target.style.borderColor = colors.primary} onBlur={e => e.target.style.borderColor = colors.border} />
                <button type="button" onClick={() => setShowSenha(!showSenha)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: colors.textDim, padding: 4 }}>
                  {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {erro && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, color: colors.danger, fontSize: 13 }}>
                <AlertCircle size={16} /> {erro}
              </div>
            )}

            {resetMsg?.sucesso && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '12px 14px', borderRadius: 8, background: `${colors.success}15`, border: `1px solid ${colors.success}30`, color: colors.success, fontSize: 13 }}>
                <CheckCircle size={16} /> {resetMsg.message}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
              background: loading ? colors.textDim : `linear-gradient(135deg, ${colors.success}, ${colors.successDark})`,
              color: '#fff', fontSize: 14, fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
            }}>{loading ? 'Alterando...' : 'Definir Nova Senha'}</button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: colors.textDim }}>
          XMiles Controle Financeiro v1.0
        </div>
      </div>
    </div>
  );
}
